import { useEffect, useState, useMemo } from 'react'
import { getRiskScoresByAssetsLabelled } from 'api'
import {
  Container,
  DataGridTable,
  Blade,
  TypoGraph,
  Divider,
  RangeColorCircle,
  HeaderBar,
  Chip,
  MultiLineChart,
  Card,
  Spinner,
} from 'components'
import moment from 'moment'
import NbDetailsSummary from '../NbDetailsSummary'
import styles from './carrierAndAgentNbDetails.module.scss'
import colors from 'theme/colors'
import strings from 'strings'
import { forEach } from 'lodash'
import { useGridApiRef } from '@mui/x-data-grid'
import carrierAndAgentNbDetailsTableData from './carrierAndAgentNbDetailsTableData'
import { isCarrierLogin } from 'tools/auth'
import { exportTableAsCSV } from 'tools/helper'
import { reportException } from 'tools/logs'
import type {
  UnderwritersScoreRiskClassificationResponse,
  RiskScoresByAssetsLabelledResponse,
  RiskScoresByAssetsSummaryResponse,
  RiskScoreDashboardDetailResponse,
} from 'api/types/dashboard'
import type { BlockRange } from 'components/rangeColorCircle'
import { GridRenderCellParams } from '@mui/x-data-grid'

const e = strings.CSV_EXPORT
const RANGE_COLOR_CIRCLE_SIZE = 16

/** Build ranges and colors from API classification (worst → best: veryHigh, high, medium, low). */
function buildRangesFromClassification(
  classification: UnderwritersScoreRiskClassificationResponse | undefined
): { ranges: BlockRange[]; colors: string[] } | null {
  if (!classification) return null
  const bands: Array<{ lower: number; upper: number }> = []
  const order: (keyof UnderwritersScoreRiskClassificationResponse)[] = [
    'veryHighRisk',
    'highRisk',
    'mediumRisk',
    'lowRisk',
  ]
  for (const key of order) {
    const band = classification[key]
    if (band && band.lowerLimit != null && band.upperLimit != null) {
      bands.push({ lower: band.lowerLimit, upper: band.upperLimit })
    }
  }
  if (bands.length === 0) return null
  const ranges: BlockRange[] = bands.map((b) => [b.lower, b.upper])
  const colors =
    ranges.length === 3
      ? ['rgb(240, 66, 66)', 'rgb(231, 176, 8)', 'rgb(33, 196, 93)']
      : ranges.length === 4
        ? ['rgb(240, 66, 66)', 'rgb(249, 116, 21)', 'rgb(231, 176, 8)', 'rgb(33, 196, 93)']
        : bands.map(() => 'rgb(158, 158, 158)')
  return { ranges, colors }
}

interface ICarrierAndNBAgentsDetailsTabProps {
  useKilometers?: boolean
}

const CarrierAndNBAgentsDetailsTab: React.FC<ICarrierAndNBAgentsDetailsTabProps> = ({ useKilometers = true }) => {
  const [loading, setLoading] = useState(false)
  const [showAssetDetails, setShowAssetDetails] = useState(false)
  const [selectedRow, setSelectedRow] = useState<any>()
  const [responseData, setResponseData] = useState<RiskScoresByAssetsLabelledResponse | undefined>()
  const [riskAssetData, setRiskAssetData] = useState<RiskScoresByAssetsLabelledResponse['assetsDetails']>([])
  const [summaryData, setSummaryData] = useState<RiskScoresByAssetsSummaryResponse | undefined>()
  const [riskData, setRiskData] = useState<RiskScoreDashboardDetailResponse[]>([])
  const [fleetPerformanceData, setFleetPerformanceData] = useState<any[]>([])
  const isCarrier = isCarrierLogin()
  const { column: baseColumns, groups } = carrierAndAgentNbDetailsTableData(isCarrier, useKilometers)
  const apiRef = useGridApiRef()

  const riskClassificationRanges = useMemo(
    () => buildRangesFromClassification(responseData?.underwritersScoreRiskClassification),
    [responseData?.underwritersScoreRiskClassification]
  )

  const INDEX_COLUMNS_WITH_CIRCLE = ['score', 'safetyIndex', 'speedingIndex', 'geoIndex'] as const
  const getIndexValue = (
    field: (typeof INDEX_COLUMNS_WITH_CIRCLE)[number],
    row: any
  ): number | undefined => {
    switch (field) {
      case 'score':
        return row?.score != null ? Number(row.score) : undefined
      case 'safetyIndex':
        return row?.harsh?.index != null ? Number(row.harsh.index) : undefined
      case 'speedingIndex':
        return row?.speed?.index != null ? Number(row.speed.index) : undefined
      case 'geoIndex':
        return (row?.geo?.index ?? row?.geographical?.index) != null
          ? Number(row?.geo?.index ?? row?.geographical?.index)
          : undefined
      default:
        return undefined
    }
  }

  const column = useMemo(() => {
    if (!riskClassificationRanges) return baseColumns
    const { ranges, colors } = riskClassificationRanges
    return baseColumns.map((col: any) => {
      if (!INDEX_COLUMNS_WITH_CIRCLE.includes(col.field)) return col
      const field = col.field as (typeof INDEX_COLUMNS_WITH_CIRCLE)[number]
      const originalRenderCell = col.renderCell
      return {
        ...col,
        renderCell: (params: GridRenderCellParams) => {
          const value = getIndexValue(field, params.row)
          if (value === undefined || value === null) {
            return originalRenderCell ? originalRenderCell(params) : (
              <TypoGraph content={strings.TABLE_DEFAULTS?.DASH_VALUE ?? '—'} />
            )
          }
          return (
            <span
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'flex-start',
                paddingTop: '4px',
              }}
            >
              <RangeColorCircle
                value={value}
                ranges={ranges}
                colors={colors}
                size={RANGE_COLOR_CIRCLE_SIZE}
              />
            </span>
          )
        },
      }
    })
  }, [baseColumns, riskClassificationRanges])

  useEffect(() => {
    setLoading(true)
    getRiskScoresByAssetsLabelled(isCarrier, { useKilometers, includeSummary: true, includeRiskScores: true })
      .then((res: any) => {
        setResponseData(res)
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (responseData) {
      setRiskAssetData(responseData.assetsDetails)
      setSummaryData(responseData?.summary)
    }
  }, [responseData])

  useEffect(() => {
    const eldProviderRisk = localStorage.getItem('eldProviderRisk')
    const filteredRiskData = responseData?.riskScores?.filter((item: any) =>
      !eldProviderRisk || eldProviderRisk.includes(item?.eldProvider ?? '')
    ) ?? []
    setRiskData(filteredRiskData)
  }, [summaryData])

  useEffect(() => {
    const graphData = getPerformanceData(riskData)
    setFleetPerformanceData(graphData)
  }, [riskData])

  const axisTitles = {
    yAxis: 'Index',
    xAxis: 'Score Date',
  }

  const chartColors = ['#7B1FA2', '#212121', '#795548', '#9E9E9E']

  function formatDate(inputDateString: string) {
    const inputDate = new Date(inputDateString)
    const year = inputDate.getFullYear()
    const month = String(inputDate.getMonth() + 1).padStart(2, '0')
    const day = String(inputDate.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getPerformanceData = (data: any[]): any[] => {
    const summaryArray: Array<{ x: string; y: number }> = []
    const harshIndexArray: Array<{ x: string; y: number }> = []
    const geoIndexArray: Array<{ x: string; y: number }> = []
    const speedIndexArray: Array<{ x: string; y: number }> = []
    forEach([...(data ?? [])].reverse(), (item: any) => {
      const rowDate = formatDate(item?.periodEndDate ?? '')
      summaryArray.push({ x: rowDate, y: item?.fleetScore ?? 0 })
      harshIndexArray.push({ x: rowDate, y: item?.harsh?.index ?? 0 })
      geoIndexArray.push({ x: rowDate, y: item?.geographical?.index ?? 0 })
      speedIndexArray.push({ x: rowDate, y: item?.speed?.index ?? 0 })
    })
    return [
      { id: 'Summary', label: 'Summary', color: '#7B1FA2', data: summaryArray },
      { id: 'Harsh Index', label: 'Harsh Index', color: '#212121', data: harshIndexArray },
      { id: 'Geo Index', label: 'Geo Index', color: '#795548', data: geoIndexArray },
      { id: 'Speeding Index', label: 'Speeding Index', color: '#9E9E9E', data: speedIndexArray },
    ]
  }

  const HeaderDom = () => (
    <Container className={styles.headerContainer} data-testid="carrier-and-agent-nb-details-header">
      <Container className={styles.headerTitleContainer}>
        <TypoGraph
          variant="h1"
          sx={{ marginRight: '1rem' }}
          mb={0}
          content={(isCarrier ? localStorage.getItem('carrierName') : 'Carrier Details') ?? 'Carrier Details'}
          dataTestId="carrier-and-agent-nb-details-carrier-name"
        />
        <Chip
          label={`Updated ${moment().format('dddd, MMMM Do')}`}
          variant="filled"
          sx={{ backgroundColor: '#E1FAEB', color: '#20693D' }}
          dataTestId="carrier-and-agent-nb-details-updated-chip"
        />
      </Container>
    </Container>
  )

  const loadPagination = (pageNumber: number, pageSize: number) => {
    console.log(`pagenumber: ${pageNumber}, pageSize: ${pageSize}`)
  }

  const handleRiskExport = () => {
    const csvString = apiRef.current.getDataAsCsv()
    const csvRows = csvString.split('\n')
    const modifiedCsv = csvRows.slice(1).join('\n')
    exportTableAsCSV(modifiedCsv, e.ASSETS_CSV_EXPORT_NAME)
  }

  const tableMenuItems = [
    {
      label: e.DOWNLOAD_AS_CSV,
      key: 'export',
      action: () => {
        handleRiskExport()
      },
    },
  ]

  const BladeContent = (_props: any) => {
    return (
      <Container className={styles.bladeBodyContainer}>
        <Container className={styles.bladeTabContainer}>

        </Container>
        <Container className={styles.documentsSection}>
          <TypoGraph variant="h6" content="Documents" />
          <Divider />

        </Container>
      </Container>
    )
  }

  return (
    <Container className={styles.agentsBladeBodyContainer} data-testid="carrier-and-agent-nb-details-page">
      <Container data-testid="carrier-and-agent-nb-details-content">
        <HeaderBar dom={<HeaderDom />} />
        <TypoGraph
          variant="h2"
          sx={{ marginRight: '1rem', marginTop: '-15px', marginBottom: '1rem' }}
          mb={0}
          content={localStorage.getItem('eldProviderRisk') ?? ''}
          dataTestId="carrier-and-agent-nb-details-eld-provider-title"
        />
        {/* Summary Header */}
        <NbDetailsSummary
          summaryData={summaryData}
          assetCount={riskAssetData?.length}
          loading={loading}
          testIdPrefix="carrier-and-agent-nb-details"
          styles={styles}
        />
        {/* Fleet Graph */}
        <Container className={styles.chartContainer} sx={{ marginBottom: '1rem' }} data-testid="carrier-and-agent-nb-details-fleet-performance-chart">
          <Card containerClass="flexAlignCenter overflowVisible">
            {(loading && <Spinner data-testid="carrier-and-agent-nb-details-fleet-performance-chart-loading" />) || (
              <>
                <TypoGraph content="Fleet Performance" dataTestId="carrier-and-agent-nb-details-fleet-performance-chart-title" />
                <MultiLineChart data={fleetPerformanceData} axisTitles={axisTitles} colors={chartColors} xAxisFormat={(v: string) => v.slice(5)} />
              </>
            )}
          </Card>
        </Container>
        {/* Asset Activity & Scoring */}
        <Container data-testid="carrier-and-agent-nb-details-asset-activity-table">
        <DataGridTable
          apiRef={apiRef}
          title={'Asset Activity & Scoring'}
          loading={loading}
          data={riskAssetData || []}
          columns={column}
          columnGroupingModel={groups}
          columnHeaderHeight={30}
          columnVisibilityModel={{ assetId: false }}
          totalCount={riskAssetData?.length}
          reduceHeight={342}
          checkboxSelection={false}
          multiSearchFilter={false}
          sortField={'totalMiles'}
          sortOrder={'desc'}
          getRowId={(row: any) => row?.assetId + "-" + row?.score}
          handlePagination={(pageNumber, pageSize) =>
            loadPagination(pageNumber, pageSize)
          }
          menuList={tableMenuItems}
        />
        </Container>
        <Blade
          title={selectedRow?.assetEldId}
          open={showAssetDetails}
          onClose={() => setShowAssetDetails(false)}
          bodyNode={<BladeContent />}
        />
      </Container>
    </Container>
  )
}

export default CarrierAndNBAgentsDetailsTab
